import axios, { AxiosRequestConfig } from 'axios';
import ApiService from './service';
import ApiServiceDataStore from './data';
import {
  MockTestQuestion,
  MockTestQuestions,
  MockTestResult,
} from '../models/mocktest';

/** API path for training center curriculum (backend contract). */
const TRAINING_CENTER_CURRICULUM = 'curriculum/v1/training-center';
const TRAINING_CENTER_AUTH = 'user/v1/auth/training-center';

export type TrainingCenterVerifyStatus =
  | 'ok'
  | 'no_customer'
  | 'not_test_stage'
  | 'not_scheduled'
  | 'schedule_not_reached';

export interface SendTrainingCenterOtpResponse {
  status?: 'ok' | 'no_customer';
  tempToken?: string;
  message?: string;
}

export interface VerifyTrainingCenterOtpResponse {
  status: TrainingCenterVerifyStatus;
  message?: string;
  executionToken?: string;
  scheduleId?: string;
  videoQuestions?: MockTestQuestion[];
  questioAnswer?: MockTestQuestion[];
}

interface TrainingCenterExamResponse {
  mocktestQuizType: MockTestQuestions['mocktestQuizType'];
  questioAnswer: MockTestQuestions['questioAnswer'];
  attemptId?: string;
}

export interface TrainingCenterSubmitPayload {
  questions: Array<{ questionId: string; selectedAnswer: string | null }>;
  examType?: string;
  testType?: string;
}

/** Service for Training Center Theory Test: OTP, exam load, and answer submission. */
export default class TrainingCenterTheoryTestService extends ApiService {
  constructor(store: ApiServiceDataStore) {
    super(store);
  }

  public async sendOtp(
    phone: string
  ): Promise<{ data: SendTrainingCenterOtpResponse }> {
    const url = `${this.apiDomain}/${TRAINING_CENTER_AUTH}/send-otp`;
    return axios.post<SendTrainingCenterOtpResponse>(url, { phone });
  }

  public async verifyOtp(
    tempToken: string,
    otp: string
  ): Promise<{ data: VerifyTrainingCenterOtpResponse }> {
    const url = `${this.apiDomain}/${TRAINING_CENTER_AUTH}/verify-otp`;
    return axios.post<VerifyTrainingCenterOtpResponse>(url, { tempToken, otp });
  }

  public async loadQuestions(
    executionToken: string,
    language?: string
  ): Promise<{ data: TrainingCenterExamResponse }> {
    const url = `${this.apiDomain}/${TRAINING_CENTER_CURRICULUM}/exam/mockTest`;
    const headers = this.buildExecutionHeaders(executionToken, {
      ...(language ? { 'content-language': language } : {}),
    });
    const config: AxiosRequestConfig = { headers };
    return axios.get<TrainingCenterExamResponse>(url, config);
  }

  public async loadInstructions(
    executionToken: string,
    language?: string
  ): Promise<{ data: any }> {
    const url = `${this.apiDomain}/${TRAINING_CENTER_CURRICULUM}/page/theorytest-instructions`;
    const headers = this.buildExecutionHeaders(executionToken, {
      ...(language ? { 'content-language': language } : {}),
    });
    const config: AxiosRequestConfig = { headers };
    return axios.get(url, config);
  }

  public async submitAnswers(
    executionToken: string,
    payload: TrainingCenterSubmitPayload
  ): Promise<{ data: MockTestResult }> {
    const url = `${this.apiDomain}/${TRAINING_CENTER_CURRICULUM}/submit`;
    const headers = this.buildExecutionHeaders(executionToken, {
      'Content-Type': 'application/json',
    });
    const config: AxiosRequestConfig = { headers };
    return axios.post<MockTestResult>(url, JSON.stringify(payload), config);
  }

  /** Build headers including backend-required execution token (API contract). */
  private buildExecutionHeaders(
    executionToken: string,
    extra?: Record<string, string>
  ): Record<string, string> {
    return {
      'x-trainingcenter-execution': executionToken,
      ...(extra ?? {}),
    };
  }
}
